import { type ReactNode, useMemo, useState } from 'react'
import { formatDistanceToNow } from 'date-fns'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  decisionOutcomeLabel,
  type DecisionExecutionDTO,
} from '@deepguide-ai/types'
import { FileExtensionIcon } from '@/utils/file-icons'
import { formatFileSize } from '@/utils/format-file-size'
import { stripMarkdown } from '@/pages/screenata/readiness/utils'
import {
  AlertCircle,
  FileOutput,
  CheckCircle2,
  Check,
  ChevronRight,
  CircleDashed,
  ClipboardList,
  Clock,
  Download,
  Eye,
  MinusCircle,
  Minus,
  Square,
  UserRound,
  XCircle,
  X,
} from 'lucide-react'
import { useNavigate, useParams } from '@tanstack/react-router'
import { trpc } from '@/lib/trpc'
import { cn } from '@/lib/utils'
import { useRunbookRun, type RunbookRunData } from '@/hooks/useRunbookRun'
import { RunbookLiveView } from './RunbookLiveView'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@deepguide-ai/ui/alert-dialog'
import { Button } from '@deepguide-ai/ui/button'
import { ScrollArea } from '@deepguide-ai/ui/scroll-area'
import { Skeleton } from '@deepguide-ai/ui/skeleton'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@deepguide-ai/ui/sheet'
import { ExecutionStatusBadge } from './ExecutionStatusBadge'
import { EvidenceFilePreviewDialog } from '@/components/auditor-center/EvidenceFilePreviewDialog'
import { AvatarGroup } from './AvatarGroup'
import { MemberAvatar } from './MemberAvatar'
import {
  DecisionExecutionPanel,
  hasDecisionRequestContext,
} from './DecisionExecutionPanel'
import { RunbookHumanActionPanel } from './RunbookHumanActionPanel'
import { StepRunTimeline } from './StepRunTimeline'

type Status =
  | 'passed'
  | 'failed'
  | 'error'
  | 'skipped'
  | 'running'
  | 'completed'
  | 'pending'
  | 'awaiting_human'
  | 'partial'

type ActionRow = {
  action: {
    provider: string
    action: string
    label?: string
    tool?: string
    outputVar?: string
  }
  status: Status
  error?: string
  durationMs: number
  rawOutput?: unknown
  checkResult?: {
    summary?: { totalChecked: number; passed: number; failed: number }
    failResults?: Array<{
      title: string
      resourceType: string
      resourceId: string
      severity: string
      evidence?: Record<string, unknown>
      remediation?: string
    }>
  }
}

type StepSummaryRow = {
  stepId?: string
  stepIndex: number
  instruction: string
  actions: ActionRow[]
  status: Status
  durationMs: number
}

/** #1115 — resolved ALL-mode fan-out progress for a population step. */
type FanoutMember = { id: string; name?: string | null; email?: string | null }
type FanoutProgress = {
  mode: 'ALL'
  total: number
  completedCount: number
  completed: FanoutMember[]
  outstanding: FanoutMember[]
}

type ArtifactRow = {
  id: string
  filename: string
  url: string
  uploadedAt?: string | Date
  mimeType: string
  size: number
}

type AwaitingStepRun = {
  id: string
  stepId: string
  stepKind: string
  status: string
  stepIndex?: number | null
  title?: string | null
  instruction?: string | null
  assigneeName?: string | null
  assigneeRole?: string | null
  outputs?: unknown
}

type StepDecisionDetails = {
  stepRunId: string
  decision: unknown
  comment: string | null
}

type StepFulfillmentDetails = {
  stepRunId: string
  operationKey: unknown
  disposition: unknown
  subjectExternalId: unknown
  subjectPerson: { name?: string | null; email?: string | null } | null
}

interface RunbookRunDetailProps {
  runId: string | null
  onClose: () => void
}

interface RunbookRunContentProps {
  runId: string
  /**
   * Output always stacks below the steps. `page` is the full-page run view —
   * it titles the panel with the procedure name + "Log" (vs. "Run details").
   */
  layout?: 'sheet' | 'embedded' | 'page'
  allowHumanReviewActions?: boolean
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

function formatDuration(ms: number): string {
  if (!ms) return '—'
  if (ms < 1000) return `${ms}ms`
  const seconds = ms / 1000
  if (seconds < 60) return `${seconds.toFixed(1)}s`
  const minutes = Math.floor(seconds / 60)
  const remSeconds = Math.round(seconds - minutes * 60)
  return `${minutes}m ${remSeconds}s`
}

export function isTechnicalRunArtifact(artifact: ArtifactRow): boolean {
  return artifact.filename === 'result.json' || artifact.filename === 'execution.log'
}

function runStatusForBadge(status: string): string {
  if (status === 'running' || status === 'awaiting_human') return 'pending'
  if (status === 'partial') return 'failed'
  return status
}

function statusIcon({
  status,
  small = false,
  timeline = false,
}: {
  status: Status | string
  small?: boolean
  timeline?: boolean
}) {
  const size = small ? 'h-3 w-3' : 'h-4 w-4'
  // Static icons for in-progress states — a human step can wait hours/days,
  // so a spinner wrongly implies active, about-to-finish work.
  if (status === 'awaiting_human') {
    return <Clock className={`${size} text-status-blue`} />
  }
  if (status === 'running' || status === 'pending') {
    return <CircleDashed className={`${size} text-status-blue`} />
  }
  if (status === 'passed' || status === 'completed') {
    if (timeline) return <Check className={`${size} text-status-green`} />
    return <CheckCircle2 className={`${size} text-status-green`} />
  }
  if (status === 'failed') {
    if (timeline) return <X className={`${size} text-status-red`} />
    return <XCircle className={`${size} text-status-red`} />
  }
  if (status === 'error') {
    return <AlertCircle className={`${size} text-status-red`} />
  }
  if (status === 'partial') {
    return <AlertCircle className={`${size} text-status-orange`} />
  }
  if (timeline) return <Minus className={`${size} text-text-placeholder`} />
  return <MinusCircle className={`${size} text-text-placeholder`} />
}

function formatRawOutput(raw: unknown): string | null {
  if (raw == null) return null
  if (typeof raw === 'string') return raw.trim() || null
  if (
    typeof raw === 'object' &&
    raw &&
    'text' in raw &&
    typeof (raw as { text?: unknown }).text === 'string'
  ) {
    return ((raw as { text: string }).text || '').trim() || null
  }
  try {
    return JSON.stringify(raw, null, 2)
  } catch {
    return String(raw)
  }
}

function summariseAction(action: ActionRow): string | null {
  const summary = action.checkResult?.summary
  if (summary) {
    return `${summary.passed}/${summary.totalChecked} passed`
  }
  const rawText =
    typeof action.rawOutput === 'string'
      ? action.rawOutput
      : action.rawOutput &&
          typeof action.rawOutput === 'object' &&
          'text' in action.rawOutput &&
          typeof (action.rawOutput as { text?: unknown }).text === 'string'
        ? (action.rawOutput as { text: string }).text
        : null

  if (!rawText) return null
  const trimmed = stripMarkdown(rawText)
  return trimmed.length > 120 ? `${trimmed.slice(0, 120)}…` : trimmed
}

function formatFailResultMeta(
  result: NonNullable<NonNullable<ActionRow['checkResult']>['failResults']>[number],
): string {
  const severity = result.severity ? `severity: ${result.severity}` : null
  const resource =
    result.resourceType && result.resourceId
      ? `${result.resourceType}: ${result.resourceId}`
      : null

  return [severity, resource].filter(Boolean).join(' · ')
}

function formatEvidenceSummary(evidence: Record<string, unknown>): string | null {
  const entries = Object.entries(evidence).filter(([key]) => key !== 'checkedAt')
  if (entries.length === 0) return null

  const summary = entries
    .slice(0, 2)
    .map(([key, value]) => `${key}: ${formatEvidenceValue(value)}`)
    .join(' · ')

  return entries.length > 2 ? `${summary} …` : summary
}

function formatEvidenceValue(value: unknown): string {
  if (value == null) return 'null'
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return String(value)
  }
  if (Array.isArray(value)) {
    return value.length === 0 ? '[]' : `${value.length} item${value.length === 1 ? '' : 's'}`
  }
  return 'object'
}

// #1115 — a run can have several `AWAITING_HUMAN` steps open at once (parallel
// human fan-out — e.g. HR + IT + Security on an onboarding procedure), so return
// ALL of them, ordered by step index, for the run-detail action panels.
function deriveAwaitingStepRuns(stepRuns: unknown): AwaitingStepRun[] {
  if (!Array.isArray(stepRuns)) return []
  return stepRuns
    .filter(
      (stepRun): stepRun is AwaitingStepRun =>
        !!stepRun &&
        typeof stepRun === 'object' &&
        (stepRun as { status?: string }).status === 'awaiting_human',
    )
    .sort((a, b) => (a.stepIndex ?? 0) - (b.stepIndex ?? 0))
}

function ActionRowItem({ action }: { action: ActionRow }) {
  const [expanded, setExpanded] = useState(false)
  const label =
    action.action.label ||
    action.action.tool ||
    `${action.action.provider}:${action.action.action}`
  const summary = summariseAction(action)
  const fullOutput = formatRawOutput(action.rawOutput)
  const failResults = action.checkResult?.failResults ?? []
  const outputVar = action.action.outputVar
  const toggleExpanded = () => setExpanded((value) => !value)

  return (
    <li
      className={`flex items-start gap-2 rounded-md px-4 pt-2 text-[12px] ${
        expanded && fullOutput ? 'pb-3' : 'pb-2'
      } ${
        fullOutput
          ? 'cursor-pointer hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
          : ''
      }`}
      onClick={fullOutput ? toggleExpanded : undefined}
      onKeyDown={(event) => {
        if (fullOutput && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault()
          toggleExpanded()
        }
      }}
      role={fullOutput ? 'button' : undefined}
      tabIndex={fullOutput ? 0 : undefined}
      aria-expanded={fullOutput ? expanded : undefined}
      aria-label={fullOutput ? (expanded ? 'Collapse output' : 'Expand output') : undefined}
    >
      {fullOutput ? (
        <ChevronRight
          className={`mt-0.5 h-3 w-3 shrink-0 text-text-placeholder ${
            expanded ? 'rotate-90' : ''
          }`}
          aria-hidden
        />
      ) : (
        <ChevronRight className='mt-0.5 h-3 w-3 shrink-0 text-text-placeholder' aria-hidden />
      )}
      <div className='min-w-0 flex-1'>
        <div className='flex items-baseline gap-2'>
          <span className='font-medium text-text-secondary'>{label}</span>
          <span className='text-[11px] text-text-placeholder'>
            {formatDuration(action.durationMs)}
          </span>
        </div>
        {summary && !expanded ? (
          <div className='mt-0.5 truncate text-[11px] text-muted-foreground'>
            {summary}
          </div>
        ) : null}
        {fullOutput && outputVar && !expanded ? (
          <div className='mt-1 text-[10px] text-text-placeholder'>
            → {outputVar}
          </div>
        ) : null}
        {expanded && fullOutput ? (
          <div className='mt-1'>
            <div className='text-[10px] font-semibold tracking-wider text-text-placeholder uppercase'>
              {outputVar ?? 'Output'}
            </div>
            <ScrollArea
              type='always'
              className='mt-1 max-h-[240px] rounded-sm bg-card'
              viewClassName='max-h-[240px] [&>div]:!block'
            >
              <pre className='whitespace-pre-wrap break-words p-2 text-[10px] leading-[16px] text-text-body'>
                {fullOutput}
              </pre>
            </ScrollArea>
          </div>
        ) : null}
        {action.error ? (
          <div className='mt-0.5 text-[11px] text-status-red'>
            {action.error}
          </div>
        ) : null}
        {failResults.length > 0 ? (
          <ul className='mt-2 space-y-2'>
            {failResults.map((result, index) => (
              <li
                key={`${result.resourceType}-${result.resourceId}-${index}`}
                className='rounded-sm border border-status-red/30 bg-status-red-bg px-3 py-2'
              >
                <div className='text-[11px] font-medium text-status-red'>
                  {result.title}
                </div>
                <div className='mt-1 text-[10px] text-status-red/80'>
                  {formatFailResultMeta(result)}
                </div>
                {result.remediation ? (
                  <div className='mt-1 text-[11px] text-status-red'>
                    Remediation: {result.remediation}
                  </div>
                ) : null}
                {result.evidence ? (
                  <div className='mt-1 text-[10px] text-status-red/80'>
                    {formatEvidenceSummary(result.evidence)}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
      {statusIcon({ status: action.status, small: true })}
    </li>
  )
}

/**
 * #1115 — admin progress rollup for an ALL-mode (population) human step:
 * show the completion percentage and the people who are still outstanding.
 */
const FANOUT_OUTSTANDING_LIMIT = 12

function StepMetaRow({
  icon,
  prefix,
  text,
  avatarPerson,
  trailing,
  node,
  className,
}: {
  icon: ReactNode
  prefix?: ReactNode
  text?: ReactNode
  avatarPerson?: { name?: string | null; email?: string | null } | null
  trailing?: ReactNode
  node?: ReactNode
  className?: string
}) {
  return (
    <div className={cn('min-w-0 text-[12px] leading-tight', className)}>
      <div className='flex min-w-0 items-center gap-2 text-text-subtle'>
        <span className='flex h-4 w-4 shrink-0 items-center justify-center'>
          {icon}
        </span>
        {prefix ? <span className='min-w-0 truncate'>{prefix}</span> : null}
        {avatarPerson ? <MemberAvatar size='sm' person={avatarPerson} /> : null}
        {text ? <span className='min-w-0 truncate'>{text}</span> : null}
        {trailing ? (
          <span className='ml-auto shrink-0 text-[11px] text-text-placeholder'>
            {trailing}
          </span>
        ) : null}
      </div>
      {node ? <div className='mt-1 pl-6'>{node}</div> : null}
    </div>
  )
}

function FanoutProgressSection({ progress }: { progress: FanoutProgress }) {
  const completionPercent = progress.total
    ? Math.round((progress.completedCount / progress.total) * 100)
    : 0
  const outstandingUsers = progress.outstanding.map((member) => ({
    id: member.id,
    name: member.name ?? null,
    email: member.email ?? undefined,
    image: null,
  }))
  const singleOutstandingUser = progress.outstanding[0]
  const singleOutstandingName = singleOutstandingUser
    ? singleOutstandingUser.name ||
      singleOutstandingUser.email ||
      singleOutstandingUser.id
    : null

  return (
    <div className='mt-2 flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2 text-[11px]'>
      <span className='text-text-subtle shrink-0'>
        This step requires multiple people to complete.
      </span>
      <span className='text-text-secondary shrink-0 font-medium'>
        Progress: {completionPercent}%
      </span>
      {progress.outstanding.length > 0 ? (
        <div className='flex min-w-0 items-center gap-2'>
          <span className='text-text-subtle shrink-0'>Waiting for:</span>
          {progress.outstanding.length === 1 ? (
            <span className='flex min-w-0 items-center gap-1.5'>
              <MemberAvatar
                size='xs'
                person={{
                  name: singleOutstandingUser?.name,
                  email: singleOutstandingUser?.email,
                }}
              />
              <span className='text-text-secondary min-w-0 truncate'>
                {singleOutstandingName}
              </span>
            </span>
          ) : (
            <AvatarGroup
              users={outstandingUsers}
              max={FANOUT_OUTSTANDING_LIMIT}
              size='sm'
            />
          )}
        </div>
      ) : (
        <span className='text-status-blue'>
          All assigned users have completed.
        </span>
      )}
    </div>
  )
}

/** Human-readable label for a finished human step's outcome. */
function humanStepCompletionLabel(completion: {
  kind?: string
  decision?: string
}): string {
  if (completion.kind === 'review')
    return decisionOutcomeLabel(completion.decision) ?? 'Approved'
  if (completion.kind === 'attest') return 'Attested'
  if (completion.kind === 'upload') return 'Evidence uploaded'
  return 'Completed'
}

/** Step kinds that wait on a person, not on automated tool calls. */
const HUMAN_STEP_KINDS = new Set(['review', 'upload', 'attest', 'assisted'])

/** What a live human step is waiting for (no tool-call timeline applies). */
function humanStepWaitingLabel(kind: string | null): string {
  if (kind === 'review') return 'Waiting for review & approval…'
  if (kind === 'upload') return 'Waiting for evidence upload…'
  if (kind === 'attest') return 'Waiting for attestation…'
  if (kind === 'assisted') return 'Waiting for manual completion…'
  return 'Waiting for human action…'
}

function decisionLabel(value: unknown): string {
  return typeof value === 'string'
    ? (decisionOutcomeLabel(value) ?? value)
    : 'Decision recorded'
}

function operationLabel(value: unknown): string {
  if (value === 'identity.account.ensure') return 'Create or restore an account'
  if (value === 'identity.group_membership.ensure') return 'Update group access'
  if (value === 'identity.account.suspend') return 'Suspend an account'
  return 'Complete the requested change'
}

function workStatusLabel(value: unknown): string {
  const labels: Record<string, string> = {
    human_fulfilled_provider_verified: 'Completed and checked in the provider',
    human_fulfilled_artifact_verified: 'Completed and evidence reviewed',
    already_compliant_provider_verified: 'Already complete and checked',
    manual_pending: 'Waiting for completion',
    denied_by_guard: 'Stopped by a safety check',
    verification_failed: 'Could not confirm the change',
    unresolved: 'Needs attention',
  }
  return labels[String(value)] ?? 'Status unavailable'
}

function UnmatchedDecisionExecutionRows({
  decisions,
  fulfillments,
}: {
  decisions: StepDecisionDetails[]
  fulfillments: StepFulfillmentDetails[]
}) {
  if (decisions.length === 0 && fulfillments.length === 0) return null

  return (
    <div className='space-y-2'>
      {decisions.map((decision) => {
        const isNegativeDecision =
          typeof decision.decision === 'string' &&
          ['reject', 'fail'].includes(decision.decision.toLowerCase())
        const comment = decision.comment
        return (
          <StepMetaRow
            key={`decision-${decision.stepRunId}`}
            icon={
              isNegativeDecision ? (
                <XCircle className='h-3.5 w-3.5 text-status-red' />
              ) : (
                <CheckCircle2 className='h-3.5 w-3.5 text-status-green' />
              )
            }
            prefix={
              <span
                className={
                  isNegativeDecision ? 'text-status-red' : 'text-status-green'
                }
              >
                Decision: {decisionLabel(decision.decision)}
              </span>
            }
            node={
              comment ? (
                <div className='truncate text-[11px] leading-relaxed text-text-subtle'>
                  {comment}
                </div>
              ) : undefined
            }
          />
        )
      })}
      {fulfillments.map((fulfillment) => (
        <StepMetaRow
          key={`fulfillment-${fulfillment.stepRunId}`}
          icon={<CheckCircle2 className='h-3.5 w-3.5 text-status-green' />}
          prefix={
            <span className='inline-flex min-w-0 items-center gap-1 truncate'>
              <span>{operationLabel(fulfillment.operationKey)}</span>
              <span>·</span>
              <span>{workStatusLabel(fulfillment.disposition)}</span>
              {fulfillment.subjectExternalId ? (
                <>
                  <span>·</span>
                  <span>Account reference:</span>
                </>
              ) : null}
            </span>
          }
          avatarPerson={fulfillment.subjectPerson}
          text={
            fulfillment.subjectExternalId ? (
              <span className='font-medium text-text-secondary'>
                {String(fulfillment.subjectExternalId)}
              </span>
            ) : null
          }
        />
      ))}
    </div>
  )
}

function StepCard({
  step,
  stepRunId,
  executionId,
  stepKind,
  liveTimeline,
  fanoutProgress,
  completion,
  decision,
  fulfillment,
  completedByName,
  completedAt,
  assigneeName,
  assigneeRole,
  assignedAt,
  variant = 'card',
  isLast = false,
}: {
  step: StepSummaryRow
  stepRunId: string | null
  executionId: string
  stepKind: string | null
  /** True while the step is still running — drive timeline streaming. */
  liveTimeline: boolean
  fanoutProgress: FanoutProgress | null
  /** Completion record for a finished human step (review/attest/upload). */
  completion: unknown
  decision: StepDecisionDetails | null
  fulfillment: StepFulfillmentDetails | null
  completedByName: string | null
  completedAt: string | null
  /** Who the step is assigned to (resolved name) and when. */
  assigneeName: string | null
  assigneeRole: string | null
  assignedAt: string | null
  /** `card` = bordered card (run-log page). `timeline` = borderless row on a
   *  status rail — avoids the card-in-card look when steps nest inside the task
   *  Activity feed's run disclosure. */
  variant?: 'card' | 'timeline'
  /** Timeline variant only — drop the connecting rail below the last step. */
  isLast?: boolean
}) {
  const completionRecord =
    completion && typeof completion === 'object'
      ? (completion as {
          kind?: string
          decision?: string
          reason?: string
          notes?: string
          statement?: string
        })
      : null
  const completionNote = completionRecord
    ? completionRecord.reason || completionRecord.notes || decision?.comment || null
    : null
  const attestedStatement =
    completionRecord?.kind === 'attest' && completionRecord.statement
      ? completionRecord.statement
      : null
  const recordedDecision = decision?.decision ?? completionRecord?.decision
  const isNegativeDecision =
    typeof recordedDecision === 'string' &&
    ['reject', 'fail'].includes(recordedDecision.toLowerCase())
  const completionLabel = decision || completionRecord?.decision
    ? `Decision: ${decisionLabel(recordedDecision)}`
    : completionRecord
      ? humanStepCompletionLabel(completionRecord)
      : null
  const isHumanStep = stepKind != null && HUMAN_STEP_KINDS.has(stepKind)
  // Who is responsible for this step. Prefer the resolved person; fall back to
  // the assigned role (e.g. an unfilled "Security Officer" group step).
  const assigneeLabel = assigneeName ?? assigneeRole ?? null

  const content = (
    <>
      <div className='flex items-baseline justify-between gap-2'>
        <div className='text-[13px] font-medium text-text-secondary'>
          Step {step.stepIndex}
        </div>
        <div className='shrink-0 text-[11px] text-text-subtle'>
          {formatDuration(step.durationMs)}
        </div>
      </div>
      <div className='mt-0.5 text-[12px] text-text-body'>{step.instruction}</div>
      {assigneeLabel ? (
        <StepMetaRow
          className='mt-1.5'
          icon={<UserRound className='h-3.5 w-3.5 text-text-subtle' />}
          prefix='Assigned to'
          text={
            <span className='inline-flex min-w-0 items-center gap-1 truncate'>
              <span className='font-medium text-text-secondary'>
                {assigneeLabel}
              </span>
              {assigneeName && assigneeRole ? (
                <span className='text-[11px] text-text-subtle'>
                  · {assigneeRole}
                </span>
              ) : null}
            </span>
          }
          avatarPerson={assigneeName ? { name: assigneeName } : null}
          trailing={
            assignedAt
              ? formatDistanceToNow(new Date(assignedAt), { addSuffix: true })
              : undefined
          }
        />
      ) : null}
      {completionRecord || decision ? (
        <StepMetaRow
          className='mt-2'
          icon={
            isNegativeDecision ? (
              <XCircle className='h-3.5 w-3.5 text-status-red' />
            ) : (
              <CheckCircle2 className='h-3.5 w-3.5 text-status-green' />
            )
          }
          prefix={
            <span
              className={
                isNegativeDecision ? 'text-status-red' : 'text-status-green'
              }
            >
              {completionLabel}
              {completedByName ? (
                <span className='ml-1 text-text-subtle'>by</span>
              ) : null}
            </span>
          }
          avatarPerson={completedByName ? { name: completedByName } : null}
          text={
            completedByName ? (
              <span className='font-medium text-text-secondary'>
                {completedByName}
              </span>
            ) : null
          }
          trailing={
            completedAt ? new Date(completedAt).toLocaleString() : undefined
          }
          node={
            completionNote || decision?.comment || attestedStatement ? (
              <div className='space-y-1 text-[11px] leading-relaxed text-text-subtle'>
                {completionNote ?? decision?.comment ? (
                  <div className='truncate'>
                    {completionNote ?? decision?.comment}
                  </div>
                ) : null}
                {attestedStatement ? (
                  <div className='italic'>“{attestedStatement}”</div>
                ) : null}
              </div>
            ) : undefined
          }
        />
      ) : null}

      {fulfillment ? (
        <StepMetaRow
          className='mt-2'
          icon={<CheckCircle2 className='h-3.5 w-3.5 text-status-green' />}
          prefix={
            <span className='inline-flex min-w-0 items-center gap-1 truncate'>
              <span>{operationLabel(fulfillment.operationKey)}</span>
              <span>·</span>
              <span>{workStatusLabel(fulfillment.disposition)}</span>
              {fulfillment.subjectExternalId ? (
                <>
                  <span>·</span>
                  <span>Account reference:</span>
                </>
              ) : null}
            </span>
          }
          avatarPerson={fulfillment.subjectPerson}
          text={
            <span className='font-medium text-text-secondary'>
              {fulfillment.subjectExternalId
                ? String(fulfillment.subjectExternalId)
                : ''}
            </span>
          }
        />
      ) : null}

      {fanoutProgress ? (
        <FanoutProgressSection progress={fanoutProgress} />
      ) : null}
    </>
  )

  const footerInner =
    liveTimeline && stepRunId && !isHumanStep ? (
      <StepRunTimeline
        stepRunId={stepRunId}
        executionId={executionId}
        enabled={liveTimeline}
        emptyLabel='Running…'
      />
    ) : liveTimeline && isHumanStep ? (
      <div className='px-4 py-3 text-center text-[12px] text-text-placeholder'>
        {humanStepWaitingLabel(stepKind)}
      </div>
    ) : step.actions.length > 0 ? (
      <ul>
        {step.actions.map((action, index) => (
          <ActionRowItem key={index} action={action} />
        ))}
      </ul>
    ) : null

  if (variant === 'timeline') {
    return (
      <li className='relative flex gap-3'>
        <div className='flex flex-col items-center'>
          <div className='pt-0.5'>
            {statusIcon({ status: step.status, timeline: true })}
          </div>
          {!isLast ? <div className='mt-1 w-px flex-1 bg-border' /> : null}
        </div>
        <div className='min-w-0 flex-1 pb-10'>
          {content}
          {footerInner ? (
            <div className='mt-3 rounded-md bg-surface-subtle'>
              {footerInner}
            </div>
          ) : null}
        </div>
      </li>
    )
  }

  return (
    <li className='rounded-md border border-border bg-card'>
      <div className='flex items-start gap-3 px-4 py-3'>
        <div className='pt-0.5'>{statusIcon({ status: step.status })}</div>
        <div className='min-w-0 flex-1'>{content}</div>
      </div>
      {footerInner ? (
        <div className='border-t border-border'>{footerInner}</div>
      ) : null}
    </li>
  )
}

function RunArtifactsSection({
  businessArtifacts,
  technicalArtifacts,
}: {
  businessArtifacts: ArtifactRow[]
  technicalArtifacts: ArtifactRow[]
}) {
  const allArtifacts = [...businessArtifacts, ...technicalArtifacts]
  const [previewArtifactId, setPreviewArtifactId] = useState<string | null>(null)
  const previewArtifact = allArtifacts.find(
    (artifact) => artifact.id === previewArtifactId,
  )

  const renderArtifactCard = (artifact: ArtifactRow) => {
    return (
      <div
        key={artifact.id}
        className='flex items-center gap-2 border-b border-border px-3 py-2 transition-colors last:border-b-0 hover:bg-surface-subtle'
      >
        <div className='flex h-7 w-7 shrink-0 items-center justify-center'>
          <FileExtensionIcon filename={artifact.filename}  />
        </div>
        <div className='min-w-0 flex-1'>
          <div className='truncate text-[13px] font-medium text-text-secondary'>
            {artifact.filename}
          </div>
          <div className='mt-0.5 truncate text-[11px] text-text-subtle'>
            {formatFileSize(artifact.size)}
          </div>
        </div>

        <div className='flex shrink-0 items-center gap-1.5'>
          <Button
            type='button'
            variant='ghost'
            size='icon'
            className='h-8 w-8'
            onClick={() => {
              setPreviewArtifactId(artifact.id)
            }}
            aria-label={`Preview ${artifact.filename}`}
            title='Preview'
          >
            <Eye className='h-3.5 w-3.5' aria-hidden />
          </Button>
          <Button asChild variant='ghost' size='icon' className='h-8 w-8'>
            <a
              href={artifact.url}
              download={artifact.filename}
              aria-label={`Download ${artifact.filename}`}
              title='Download'
            >
              <Download className='h-3.5 w-3.5' aria-hidden />
            </a>
          </Button>
        </div>
      </div>
    )
  }

  if (allArtifacts.length === 0) {
    return (
      <section className='rounded-md border border-border bg-card'>
        <div className='px-4 py-6 text-center text-[12px] text-text-placeholder'>
          No artifacts available for this run.
        </div>
      </section>
    )
  }

  return (
    <div className='space-y-3'>
      {businessArtifacts.length > 0 ? (
        <section className='overflow-hidden rounded-md border border-border bg-card'>
          <div className='divide-y divide-border'>
            {businessArtifacts.map(renderArtifactCard)}
          </div>
        </section>
      ) : (
        <section className='rounded-md border border-border bg-card'>
          <div className='px-4 py-4 text-[12px] text-text-subtle'>
            No business output file was produced for this run.
          </div>
        </section>
      )}

      {technicalArtifacts.length > 0 ? (
        <div className='space-y-1.5'>
          <div className='px-1 text-[11px] font-medium tracking-wide text-text-placeholder uppercase'>
            Technical artifacts
          </div>
          <section className='overflow-hidden rounded-md border border-border bg-card'>
            <div className='divide-y divide-border'>
              {technicalArtifacts.map(renderArtifactCard)}
            </div>
          </section>
        </div>
      ) : null}

      <EvidenceFilePreviewDialog
        file={previewArtifact ?? null}
        onClose={() => setPreviewArtifactId(null)}
      />
    </div>
  )
}

/** Pretty-print a resolved output value (string as-is, structures as JSON). */
function formatRunOutputValue(value: unknown): string {
  if (value == null) return '—'
  if (typeof value === 'string') return value
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return String(value)
  }
}

/**
 * The runbook's author-declared outputs (its return signature), resolved at run
 * completion and stored under `execution.outputs.__outputs__`. Shown as the
 * run's typed result above the raw artifacts.
 */
function RunOutputsSection({
  outputs,
}: {
  outputs: Array<{ name: string; value: unknown }>
}) {
  if (outputs.length === 0) return null
  return (
    <section className='rounded-md border border-border bg-card'>
      <div className='flex items-center gap-2 border-b border-border px-4 py-2.5'>
        <span className='text-[12px] font-semibold text-text-secondary'>Outputs</span>
        <span className='text-[11px] text-text-placeholder'>the runbook&apos;s result</span>
      </div>
      <div className='space-y-1.5 p-3'>
        {outputs.map((output) => (
          <div
            key={output.name}
            className='rounded-md border border-border bg-surface-subtle px-2.5 py-2'
          >
            <div className='mb-0.5 font-mono text-[11px] font-medium text-text-secondary'>
              {output.name}
            </div>
            <ScrollArea className='max-h-40'>
              <div className='text-text-body font-mono text-[11px] break-words whitespace-pre-wrap'>
                {formatRunOutputValue(output.value)}
              </div>
            </ScrollArea>
          </div>
        ))}
      </div>
    </section>
  )
}

// RFC-206 §7.4 — the one sanctioned run→Task materialization: spawn a linked
// follow-up Task for work that must happen OUTSIDE the run. Idempotent server-
// side on `run:{executionId}`, so repeated clicks reopen the same task.
function CreateFollowUpTaskButton({ executionId }: { executionId: string }) {
  const navigate = useNavigate()
  const { workspaceId } = useParams({ strict: false }) as {
    workspaceId?: string
  }
  const createMutation = useMutation({
    ...trpc.task.createFromRun.mutationOptions(),
    onSuccess: (data: {
      task?: { id: string; displayCode?: string }
      created?: boolean
    }) => {
      toast.success(
        data.created
          ? `Follow-up task created (${data.task?.displayCode ?? ''})`
          : 'This run already has a follow-up task. Opening it.'
      )
      if (workspaceId && data.task?.id) {
        navigate({
          to: '/workspaces/$workspaceId/tasks/$taskId',
          params: { workspaceId, taskId: data.task.id },
          search: { category: 'manual' },
        })
      }
    },
    onError: (error: { message?: string }) => {
      toast.error(error.message ?? 'Failed to create follow-up task')
    },
  })

  return (
    <Button
      size='sm'
      variant='default'
      className='h-8 px-2.5 text-[12px]'
      disabled={createMutation.isPending}
      onClick={() => createMutation.mutate({ executionId })}
    >
      <ClipboardList className='mr-1 h-4 w-4' />
      Create follow-up task
    </Button>
  )
}

export function RunbookRunHeaderActions({ runId }: { runId: string }) {
  const runQuery = useQuery({
    ...trpc.runbook.getRun.queryOptions({ id: runId }),
    enabled: !!runId,
  })
  const status = runQuery.data?.status

  if (status !== 'failed' && status !== 'partial') return null

  return <CreateFollowUpTaskButton executionId={runId} />
}

function CancelRunButton({ executionId }: { executionId: string }) {
  const queryClient = useQueryClient()
  const cancelMutation = useMutation({
    ...trpc.runbook.cancel.mutationOptions(),
    onSuccess: () => {
      toast.success('Run cancelled')
      queryClient.invalidateQueries({
        queryKey: trpc.runbook.getRun.queryKey({ id: executionId }),
      })
      queryClient.invalidateQueries({
        queryKey: trpc.runbook.listRuns.queryKey(),
      })
      // AWAITING_HUMAN cancels archive the run's inbox items via
      // archiveHumanActionInboxItemsForExecution. Invalidate inbox
      // queries so the action card disappears immediately, matching
      // the precedent set by RunbookHumanActionPanel.
      queryClient.invalidateQueries({
        queryKey: trpc.inbox.list.queryKey(),
      })
      queryClient.invalidateQueries({
        queryKey: trpc.inbox.actionRequiredCount.queryKey(),
      })
    },
    onError: (error) => {
      toast.error(`Failed to cancel run: ${error.message}`)
    },
  })

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          size='sm'
          variant='outline'
          className='h-7 px-2 text-[11px] text-status-red hover:text-status-red hover:bg-status-red-bg'
          disabled={cancelMutation.isPending}
        >
          <Square className='h-3 w-3 ' />
          Cancel
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Cancel this run?</AlertDialogTitle>
          <AlertDialogDescription>
            The current step will stop at the next safe boundary. Completed
            steps and their evidence will be preserved on the run timeline.
            You can re-run the procedure later.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Keep running</AlertDialogCancel>
          <AlertDialogAction
            onClick={() =>
              cancelMutation.mutate({
                executionId,
                reason: 'Cancelled by user',
              })
            }
            className='bg-status-red hover:bg-status-red/90'
          >
            Cancel run
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

/**
 * The chronological steps panel of a runbook run — the `<ol>` of `StepCard`s
 * (per-step status, live tool-call timeline, human-step completion). Reused by
 * the full run-log view (`RunbookRunContent`, which passes its already-fetched
 * `run` to avoid a second subscription) and by the task Activity feed (which
 * passes only `runId` and lets it fetch). Single source of truth for step
 * rendering — do not re-derive the steps list elsewhere.
 */
export function RunbookStepsList({
  runId,
  run: providedRun,
  decisionExecution,
  variant = 'card',
  hasTrailingOutput = false,
}: {
  runId: string
  /** Pass an already-fetched run to skip the internal query + SSE stream. */
  run?: RunbookRunData
  decisionExecution?: DecisionExecutionDTO | null
  /** `card` (run-log page) or `timeline` (task Activity feed) — see StepCard. */
  variant?: 'card' | 'timeline'
  /** Keep the final timeline rail connected to the output node when present. */
  hasTrailingOutput?: boolean
}) {
  const fetched = useRunbookRun({
    executionId: runId,
    enabled: !providedRun,
    fallbackPollMs: 1000,
  })
  const run = providedRun ?? fetched.data

  const steps = useMemo(
    () =>
      Array.isArray(run?.results)
        ? (run.results as StepSummaryRow[]).map((step, index) => ({
            ...step,
            // Number by EXECUTION order, not the authoring `stepIndex`. Step
            // summaries are pushed in the executor's (topological) run order, so
            // the array position is the real execution position — a step wired to
            // run first (e.g. a leading human-approval step) shows "Step 1" even
            // if it was authored last.
            stepIndex: index + 1,
          }))
        : [],
    [run?.results],
  )
  const stepRunByStepId = useMemo(() => {
    const map = new Map<
      string,
      {
        id: string
        status: string
        stepKind: string | null
        fanoutProgress: FanoutProgress | null
        completion: unknown
        completedByName: string | null
        completedAt: string | null
        assigneeName: string | null
        assigneeRole: string | null
        assignedAt: string | null
      }
    >()
    const list = Array.isArray(run?.stepRuns) ? run!.stepRuns : []
    for (const stepRun of list as Array<{
      id: string
      stepId: string
      status: string
      stepKind?: string | null
      fanoutProgress?: FanoutProgress | null
      completion?: unknown
      completedByName?: string | null
      completedAt?: string | null
      assigneeName?: string | null
      assigneeRole?: string | null
      assignedAt?: string | null
    }>) {
      if (stepRun?.stepId && stepRun?.id) {
        map.set(stepRun.stepId, {
          id: stepRun.id,
          status: stepRun.status,
          stepKind: stepRun.stepKind ?? null,
          fanoutProgress: stepRun.fanoutProgress ?? null,
          completion: stepRun.completion ?? null,
          completedByName: stepRun.completedByName ?? null,
          completedAt: stepRun.completedAt ?? null,
          assigneeName: stepRun.assigneeName ?? null,
          assigneeRole: stepRun.assigneeRole ?? null,
          assignedAt: stepRun.assignedAt ?? null,
        })
      }
    }
    return map
  }, [run?.stepRuns])
  const decisionByStepRunId = useMemo(() => {
    const map = new Map<string, StepDecisionDetails>()
    for (const approval of decisionExecution?.approvals ?? []) {
      const stepRunId = approval.stepRunId
      if (typeof stepRunId !== 'string') continue
      map.set(stepRunId, {
        stepRunId,
        decision: approval.decision,
        comment: typeof approval.comment === 'string' ? approval.comment : null,
      })
    }
    return map
  }, [decisionExecution?.approvals])
  const fulfillmentByStepRunId = useMemo(() => {
    const map = new Map<string, StepFulfillmentDetails>()
    for (const fulfillment of decisionExecution?.fulfillment ?? []) {
      const stepRunId = fulfillment.stepRunId
      if (typeof stepRunId !== 'string') continue
      const subjectExternalId =
        typeof fulfillment.subjectExternalId === 'string'
          ? fulfillment.subjectExternalId
          : null
      const matchedPrincipal = subjectExternalId
        ? decisionExecution?.requestContext?.externalPrincipals.find(
            (principal) => principal.externalId === subjectExternalId,
          )
        : null
      const subjectEmail =
        matchedPrincipal?.email ??
        decisionExecution?.requestContext?.subject?.email ??
        null
      map.set(stepRunId, {
        stepRunId,
        operationKey: fulfillment.operationKey,
        disposition: fulfillment.disposition,
        subjectExternalId,
        subjectPerson:
          subjectEmail || subjectExternalId
            ? { name: subjectEmail ?? subjectExternalId, email: subjectEmail }
            : null,
      })
    }
    return map
  }, [decisionExecution?.fulfillment, decisionExecution?.requestContext])
  const renderedStepRunIds = useMemo(() => {
    const ids = new Set<string>()
    for (const step of steps) {
      const stepRun = stepRunByStepId.get(step.stepId ?? '')
      if (stepRun) ids.add(stepRun.id)
    }
    return ids
  }, [stepRunByStepId, steps])
  const unmatchedDecisions = useMemo(
    () =>
      Array.from(decisionByStepRunId.values()).filter(
        (row) => !renderedStepRunIds.has(row.stepRunId),
      ),
    [decisionByStepRunId, renderedStepRunIds],
  )
  const unmatchedFulfillments = useMemo(
    () =>
      Array.from(fulfillmentByStepRunId.values()).filter(
        (row) => !renderedStepRunIds.has(row.stepRunId),
      ),
    [fulfillmentByStepRunId, renderedStepRunIds],
  )
  const unmatchedDecisionRows = (
    <UnmatchedDecisionExecutionRows
      decisions={unmatchedDecisions}
      fulfillments={unmatchedFulfillments}
    />
  )
  const hasUnmatchedDecisionRows =
    unmatchedDecisions.length > 0 || unmatchedFulfillments.length > 0

  if (!run && fetched.isLoading) {
    return (
      <div className='space-y-3 py-3'>
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={index} className='flex items-center gap-3'>
            <Skeleton className='size-5 shrink-0 rounded-full' />
            <Skeleton className='h-4 flex-1' />
          </div>
        ))}
      </div>
    )
  }
  if (steps.length === 0) {
    return (
      <div className='space-y-3'>
        <div className='py-6 text-center text-[12px] text-text-placeholder'>
          {run?.status === 'running'
            ? 'Waiting for the first step…'
            : 'No steps recorded.'}
        </div>
        {hasUnmatchedDecisionRows ? unmatchedDecisionRows : null}
      </div>
    )
  }

  return (
    <>
      <ol className={variant === 'timeline' ? 'space-y-0' : 'space-y-3'}>
        {steps.map((step, index) => {
          const stepRun = stepRunByStepId.get(step.stepId ?? '') ?? null
          const liveTimeline =
            !!stepRun &&
            (stepRun.status === 'running' ||
              stepRun.status === 'pending' ||
              step.status === 'running')
          return (
            <StepCard
              key={`${step.stepIndex}-${step.stepId ?? step.instruction}`}
              step={step}
              stepRunId={stepRun?.id ?? null}
              executionId={runId}
              stepKind={stepRun?.stepKind ?? null}
              liveTimeline={liveTimeline}
              fanoutProgress={stepRun?.fanoutProgress ?? null}
              completion={stepRun?.completion ?? null}
              decision={
                stepRun ? decisionByStepRunId.get(stepRun.id) ?? null : null
              }
              fulfillment={
                stepRun ? fulfillmentByStepRunId.get(stepRun.id) ?? null : null
              }
              completedByName={stepRun?.completedByName ?? null}
              completedAt={stepRun?.completedAt ?? null}
              assigneeName={stepRun?.assigneeName ?? null}
              assigneeRole={stepRun?.assigneeRole ?? null}
              assignedAt={stepRun?.assignedAt ?? null}
              variant={variant}
              isLast={index === steps.length - 1 && !hasTrailingOutput}
            />
          )
        })}
      </ol>
      {hasUnmatchedDecisionRows ? (
        <div className={variant === 'timeline' ? 'mt-2' : 'mt-3'}>
          {unmatchedDecisionRows}
        </div>
      ) : null}
    </>
  )
}

export function RunbookRunContent({
  runId,
  layout = 'sheet',
  // The run-details view is observational (admin/operator watching a run). The
  // human action is completed by the assignee on their own surface (inbox /
  // My Tasks), so don't surface the action panel here unless a caller explicitly
  // opts in (e.g. Vera chat). Previously this auto-enabled on `sheet`.
  allowHumanReviewActions = false,
}: RunbookRunContentProps) {
  const { workspaceId } = useParams({ strict: false }) as {
    workspaceId?: string
  }
  const runQuery = useRunbookRun({ executionId: runId, fallbackPollMs: 1000 })

  const run = runQuery.data
  const decisionExecutionQuery = useQuery({
    ...trpc.runbook.decisionExecution.queryOptions({ executionId: runId }),
    enabled: !!run,
  })

  // A finished browser run's replay video can finalize after the in-run capture
  // gave up — poll getRunVideo until it's ready (mirrors the workflow replay).
  const runIsTerminal = Boolean(
    run && run.status !== 'running' && run.status !== 'awaiting_human',
  )
  const wasBrowserRun = Boolean(run?.sessionProvider || run?.sessionUrl)
  const runVideoQuery = useQuery({
    ...trpc.runbook.getRunVideo.queryOptions({ id: runId }),
    enabled: runIsTerminal && wasBrowserRun && !run?.replayVideoUrl,
    refetchInterval: (query) => {
      const data = query.state.data as
        | { videoUrl?: string | null; unavailable?: boolean }
        | undefined
      // Stop polling once we have a video OR the server says it's terminal
      // (recording failed / provider host unreachable) — otherwise spin forever.
      return data?.videoUrl || data?.unavailable ? false : 3000
    },
  })
  const effectiveReplayUrl =
    run?.replayVideoUrl ?? runVideoQuery.data?.videoUrl ?? null
  const replayUnavailable =
    runIsTerminal &&
    wasBrowserRun &&
    !effectiveReplayUrl &&
    Boolean(runVideoQuery.data?.unavailable)
  // Show a "generating replay" placeholder while a finished browser run has no
  // video yet (recording still finalizing) — but not once it's terminally
  // unavailable (that shows its own message instead of spinning forever).
  const replayPending =
    runIsTerminal && wasBrowserRun && !effectiveReplayUrl && !replayUnavailable

  const stepCount = Array.isArray(run?.results) ? run.results.length : 0
  const artifacts = useMemo(
    () =>
      Array.isArray(run?.artifacts)
        ? (run.artifacts as ArtifactRow[]).filter(
            (artifact): artifact is ArtifactRow =>
              !!artifact &&
              typeof artifact === 'object' &&
              typeof artifact.id === 'string' &&
              typeof artifact.filename === 'string' &&
              typeof artifact.url === 'string' &&
              typeof artifact.mimeType === 'string' &&
              typeof artifact.size === 'number',
          )
        : [],
    [run?.artifacts],
  )
  const businessArtifacts = useMemo(
    () => artifacts.filter((artifact) => !isTechnicalRunArtifact(artifact)),
    [artifacts],
  )
  const technicalArtifacts = useMemo(
    () => artifacts.filter(isTechnicalRunArtifact),
    [artifacts],
  )
  const awaitingStepRuns = deriveAwaitingStepRuns(run?.stepRuns)

  const isPage = layout === 'page'
  const hasRunError = Boolean(
    run?.errorMessage && run.status !== 'awaiting_human'
  )
  const hasDecisionContext = hasDecisionRequestContext(
    decisionExecutionQuery.data
  )
  const headerClassName =
    layout === 'embedded'
      ? 'border-b border-border px-4 py-3'
      : 'border-b border-border px-6 pt-6 pb-4'
  const bodyClassName = cn(
    layout === 'embedded' ? 'px-4 pb-4' : 'px-6 pb-4',
    hasRunError || hasDecisionContext ? 'pt-10' : 'pt-4'
  )
  const insetClassName = layout === 'embedded' ? 'mx-4 mt-4' : 'mx-6 mt-4'
  const decisionContextClassName =
    layout === 'embedded' ? 'mx-4 mt-10 px-2' : 'mx-6 mt-10 px-2'

  // The runbook's author-declared outputs (return signature), resolved at run
  // completion and stored under `execution.outputs.__outputs__`.
  const declaredRunOutputs = useMemo(() => {
    const raw = isRecord(run?.outputs)
      ? (run!.outputs as Record<string, unknown>).__outputs__
      : undefined
    if (!isRecord(raw)) return []
    return Object.entries(raw).map(([name, value]) => ({ name, value }))
  }, [run?.outputs])

  const outputSection =
    artifacts.length > 0 || run?.status !== 'running' ? (
      <div
        className={`relative flex gap-3 ${stepCount > 0 ? '-mt-5' : ''}`}
      >
        <div className='flex w-4 shrink-0 items-start justify-center pt-0.5'>
          <FileOutput className='h-4 w-4 text-primary' aria-hidden />
        </div>
        <div className='min-w-0 flex-1 space-y-3 pb-2'>
          <div className='text-[13px] font-medium text-text-secondary'>Run output</div>
          <RunOutputsSection outputs={declaredRunOutputs} />
          <RunArtifactsSection
            businessArtifacts={businessArtifacts}
            technicalArtifacts={technicalArtifacts}
          />
        </div>
      </div>
    ) : null

  const content = (
    <>
      {run ? (
        <RunbookLiveView
          className={`${insetClassName} pt-3`}
          status={run.status}
          sessionUrl={run.sessionUrl}
          replayVideoUrl={effectiveReplayUrl}
          replayPending={replayPending}
          replayUnavailable={replayUnavailable}
        />
      ) : null}

      {awaitingStepRuns.length > 0 && allowHumanReviewActions ? (
        <div className={`${insetClassName} space-y-3`}>
          {/* #1115 — one action panel per concurrently-open human step. When more
              than one is open (parallel fan-out), prefix each with its step
              title + assignee so the operator knows which department it is. */}
          {awaitingStepRuns.map((stepRun) => (
            <div key={stepRun.id}>
              {awaitingStepRuns.length > 1 ? (
                <div className='text-text-subtle mb-1.5 flex items-center gap-1.5 text-[11px] font-medium'>
                  <span className='tracking-[0.04em] uppercase'>
                    {stepRun.title || `Step ${(stepRun.stepIndex ?? 0) + 1}`}
                  </span>
                  {stepRun.assigneeName || stepRun.assigneeRole ? (
                    <span className='text-text-placeholder'>
                      · {stepRun.assigneeName ?? stepRun.assigneeRole}
                    </span>
                  ) : null}
                </div>
              ) : null}
              <RunbookHumanActionPanel
                executionId={runId}
                statusId={run?.controlEvidenceStatusId ?? null}
                stepRun={stepRun}
              />
            </div>
          ))}
        </div>
      ) : null}

      {hasRunError ? (
        <div className={`${insetClassName} rounded-md border border-status-red/30 bg-status-red-bg px-3 py-2 text-[12px] text-status-red`}>
          {run.errorMessage}
        </div>
      ) : null}

      {hasDecisionContext ? (
        <div className={decisionContextClassName}>
          <DecisionExecutionPanel
            decision={decisionExecutionQuery.data}
            workspaceId={workspaceId}
          />
        </div>
      ) : null}

      <div className={bodyClassName}>
        {runQuery.isLoading && !run ? (
          <div className='py-8 text-center text-[12px] text-text-placeholder'>
            Loading…
          </div>
        ) : stepCount === 0 ? (
          <div className='space-y-5'>
            <div className='rounded-md border border-dashed border-border bg-surface-subtle px-4 py-8 text-center text-[12px] text-text-placeholder'>
              {run?.status === 'running'
                ? 'Waiting for the first step…'
                : 'No steps recorded.'}
            </div>
            {outputSection}
          </div>
        ) : (
          <div className='space-y-5'>
            <RunbookStepsList
              runId={runId}
              run={run ?? undefined}
              decisionExecution={decisionExecutionQuery.data}
              variant='timeline'
              hasTrailingOutput={Boolean(outputSection)}
            />
            {outputSection}
          </div>
        )}
      </div>
    </>
  )

  return (
    <>
      <div className={headerClassName}>
        <div className='font-heading text-[16px] font-semibold text-foreground'>
          {isPage && run?.workflow?.name
            ? `${run.workflow.name} Log`
            : 'Run details'}
        </div>
        {run ? (
          <div className='flex items-center gap-3 pt-1 text-[12px] text-text-body'>
            <ExecutionStatusBadge status={runStatusForBadge(run.status)} />
            <span>
              {formatDistanceToNow(new Date(run.executedAt), {
                addSuffix: true,
              })}
            </span>
            <span>{formatDuration(run.executionTime)}</span>
            {run.totalSteps > 0 ? (
              <span>
                {run.successfulSteps}/{run.totalSteps} passed
              </span>
            ) : null}
            {run.status === 'running' || run.status === 'awaiting_human' ? (
              <div className='ml-auto'>
                <CancelRunButton executionId={runId} />
              </div>
            ) : null}
            {!isPage && (run.status === 'failed' || run.status === 'partial') ? (
              <div className='ml-auto'>
                <CreateFollowUpTaskButton executionId={runId} />
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
      {layout === 'sheet' ? (
        <ScrollArea type='always' className='min-h-0 flex-1'>
          {content}
        </ScrollArea>
      ) : (
        content
      )}
    </>
  )
}

export function RunbookRunDetail({ runId, onClose }: RunbookRunDetailProps) {
  return (
    <Sheet open={!!runId} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side='right'
        className='!max-w-[600px] bg-card p-0 sm:max-w-[600px]'
      >
        {runId ? (
          <RunbookRunContent runId={runId} />
        ) : (
          <SheetHeader className='border-border border-b px-6 pt-6 pb-4'>
            <SheetTitle className='font-heading text-[16px] font-semibold text-foreground'>
              Run details
            </SheetTitle>
          </SheetHeader>
        )}
      </SheetContent>
    </Sheet>
  )
}
